import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import WeeklyPost from '../../components/WeeklyPost/WeeklyPost';
import ContactUs from '../../components/ContactUs/ContactUs';
import styles from './ArmorOfGodDetailPage.module.css';
import config from '../../config/config';
import { ArmorPostSanity } from '../../types/types';
import '../../components/PortableTextStyles.css';

const ArmorOfGodDetailPage: React.FC = () => {
  const { armorId } = useParams<{ armorId: string }>();
  const navigate = useNavigate();
  const [armorPost, setArmorPost] = useState<ArmorPostSanity | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArmorPost = async () => {
      setLoading(true);
      try {
        if (!armorId) {
          setError('No armor ID provided.');
          setLoading(false);
          return;
        }

        console.log(`Fetching armor post with ID: ${armorId}`);
        const response = await axios.get(`${config.API_BASE_URL}/armor-posts/${armorId}`);
        
        if (response.data) {
          console.log('Received armor post:', response.data);
          setArmorPost(response.data);
          setError(null);
        } else {
          setError('No data received for the requested armor post.');
        }
      } catch (error) {
        console.error('Error fetching armor post:', error);
        setError(`Failed to fetch armor post with ID "${armorId}". Please try again later.`);
        
        // Only use mock data if enabled in config
        if (config.USE_MOCK_DATA) {
          // Mock data for development/testing
          const mockArmorPost: ArmorPostSanity = {
            _id: 'mock-id',
            id: parseInt(armorId || '1'),
            title: 'Belt of Truth - Ephesians 6:14a',
            date: '2025-03-17',
            content: [
              {
                _type: 'block',
                children: [
                  {
                    _type: 'span',
                    text: 'The belt of truth represents honesty, integrity, and a commitment to God\'s truth. With the truth, we will be able to understand the tricks Satan uses to water down the Word.'
                  }
                ]
              }
            ],
            references: ['Ephesians 6:14a', 'John 17:17', 'Psalm 51:6', 'John 8:32'],
            sourceType: 'armorPost'
          };
          
          setArmorPost(mockArmorPost);
          setError(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchArmorPost();
  }, [armorId]);

  const handleBackClick = () => {
    navigate('/armor-of-god');
  };

  if (loading) {
    return (
      <div className={styles['armor-detail-page']}>
        <div className={styles['loading']}>Loading armor details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['armor-detail-page']}>
        <div className={styles['error']}>
          <h2>Error</h2>
          <p>{error}</p>
          <button className={styles['back-button']} onClick={handleBackClick}>
            Back to Armor of God
          </button>
        </div>
      </div>
    );
  }

  if (!armorPost) {
    return (
      <div className={styles['armor-detail-page']}>
        <div className={styles['error']}>
          <h2>Armor Piece Not Found</h2>
          <p>The requested armor piece could not be found.</p>
          <button className={styles['back-button']} onClick={handleBackClick}>
            Back to Armor of God
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['armor-detail-page']}>
      <div className={styles['armor-header']}>
        <button className={styles['back-button']} onClick={handleBackClick}>
          ← Back to Armor of God
        </button>
        <h1>{armorPost.title}</h1>
      </div>

      <div className={styles['armor-teaching']}>
        <WeeklyPost
          title={armorPost.title}
          date={armorPost.date}
          content={armorPost.content}
          type="armor"
          references={armorPost.references}
        />
      </div>

      <ContactUs />
    </div>
  );
};

export default ArmorOfGodDetailPage;
